import { resolvePositionConfigurations } from '@cutr/constants/windows/windows';
import {
  CONFIGURATIONS_OPTIONS,
  GlassOption,
} from '@cutr/constants/windows/windows-configurations.ts';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/blocks/Modal';
import { Button } from '@/primitives/Button';
import { Checkbox } from '@/primitives/Checkbox';
import { Edit, Icon } from '@/primitives/Icons';
import { useToggle } from '@/utils/hooks';

import { useWindowStore } from '../api/windowStore';
import { unboxLabel } from './configurations/Configurations';
import { useConfigurations } from './configurations/ConfigurationsContext';
import { ConfigurationButton } from './configurations/inputs/ConfigurationButton';
import { optionFilterBySystem } from './configurations/inputs/ConfigurationSelect';
import { FilterProvider, useFilters, useGlassFilter } from './FilterContext';
import styles from './GlassSelect.module.css';

export const GlassSelect = () => {
  const { t } = useTranslation();
  const [isOpen, toggleModal] = useToggle(false);

  const { configurations } = useConfigurations();

  return (
    <>
      <div>
        <ConfigurationButton
          label={unboxLabel(configurations.glassType?.label)}
          onClick={toggleModal}
          icon={<Icon icon={<Edit />} size={2.5} />}
        />
      </div>
      {isOpen && (
        <Modal
          title={t('reseller.glassSelection.modal.title')}
          isOpen={isOpen}
          handleClose={() => {
            toggleModal();
          }}
        >
          <FilterProvider>
            <GlassModal toggleModal={toggleModal} />
          </FilterProvider>
        </Modal>
      )}
    </>
  );
};

type GlassModalProps = {
  toggleModal: () => void;
};

const GlassModal = ({ toggleModal }: GlassModalProps) => {
  const { t } = useTranslation();
  const { configurations, dispatch } = useConfigurations();
  const { configurations: orderConfigurations } = useWindowStore();
  const mergedConfigurations = resolvePositionConfigurations({
    orderConfigurations,
    positionConfigurations: configurations,
  });
  const { filterGlassOptions } = useGlassFilter();

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toggleModal();
  };

  const optionsFilterFn = (
    option: GlassOption,
    configs: Partial<typeof configurations>
  ) => {
    // don't filter if system or thickness is not set.
    if (!configs.system || !configs.thickness) {
      return true;
    }
    return (
      optionFilterBySystem(option, configs) &&
      // glass options are also filtered by minimum thickness
      configs.thickness &&
      option.systemMinimumThickness <= configs.thickness.value
    );
  };

  const allOptions = CONFIGURATIONS_OPTIONS.glassType.options;
  const systemFilteredOptions = optionsFilterFn
    ? allOptions.filter((option) =>
        optionsFilterFn(option, mergedConfigurations)
      )
    : allOptions;

  // Apply additional filters from FilterContext
  const options = filterGlassOptions(systemFilteredOptions);

  return (
    <>
      <p>{t('reseller.glassSelection.modal.description')}</p>

      <form onSubmit={onSubmit} className="stack">
        <div className={styles.modalLayout}>
          <Filters />
          <div className={styles.glassOptions}>
            {options.map((option) => (
              <GlassItem
                key={option.value}
                value={option.value}
                label={unboxLabel(option.label)}
                onClick={(selected) => {
                  const value = options.find(
                    (option) => option.value === selected
                  )!;

                  dispatch({
                    type: 'SET_GLASS_TYPE',
                    value,
                  });
                  toggleModal();
                }}
              />
            ))}
          </div>
        </div>
        <Button type="submit">{t('common.cta.save')}</Button>
      </form>
    </>
  );
};

const Filters = () => {
  return (
    <div className={styles.filters}>
      <PanelCountFilter />
      <ExtrasFilter />
    </div>
  );
};

const PanelCountFilter = () => {
  const { t } = useTranslation();
  const { isPanelTypeActive, togglePanelType } = useFilters();

  return (
    <div className={styles.group}>
      <strong>{t('reseller.glassSelection.modal.type.label')}</strong>
      <Checkbox
        checked={isPanelTypeActive('2-layer')}
        onChange={() => {
          togglePanelType('2-layer');
        }}
      >
        {t('reseller.glassSelection.modal.type.options.doublePane')}
      </Checkbox>
      <Checkbox
        checked={isPanelTypeActive('3-layer')}
        onChange={() => {
          togglePanelType('3-layer');
        }}
      >
        {t('reseller.glassSelection.modal.type.options.triplePane')}
      </Checkbox>
    </div>
  );
};

const ExtrasFilter = () => {
  const { t } = useTranslation();
  const { isExtraActive, toggleExtra } = useFilters();

  return (
    <div className={styles.group}>
      <strong>{t('reseller.glassSelection.modal.extras.label')}</strong>
      <Checkbox
        checked={isExtraActive('antiBurglar')}
        onChange={() => {
          toggleExtra('antiBurglar');
        }}
      >
        {t('reseller.glassSelection.modal.extras.options.antiBurglary')}
      </Checkbox>
      <Checkbox
        checked={isExtraActive('temperedSafetyGlass')}
        onChange={() => {
          toggleExtra('temperedSafetyGlass');
        }}
      >
        {t('reseller.glassSelection.modal.extras.options.esg')}
      </Checkbox>
      <Checkbox
        checked={isExtraActive('ornaments')}
        onChange={() => {
          toggleExtra('ornaments');
        }}
      >
        {t('reseller.glassSelection.modal.extras.options.ornaments')}
      </Checkbox>
      <Checkbox
        checked={isExtraActive('sunProtection')}
        onChange={() => {
          toggleExtra('sunProtection');
        }}
      >
        {t('reseller.glassSelection.modal.extras.options.sunProtection')}
      </Checkbox>
      <Checkbox
        checked={isExtraActive('soundProtection')}
        onChange={() => {
          toggleExtra('soundProtection');
        }}
      >
        {t('reseller.glassSelection.modal.extras.options.soundInsulation')}
      </Checkbox>
      <Checkbox
        checked={isExtraActive('laminatedSafetyGlass')}
        onChange={() => {
          toggleExtra('laminatedSafetyGlass');
        }}
      >
        {t('reseller.glassSelection.modal.extras.options.vsg')}
      </Checkbox>
    </div>
  );
};

type GlassItemProps = {
  label: string;
  value: string;
  onClick: (selected: string) => void;
};

const GlassItem = ({ label, value, onClick }: GlassItemProps) => {
  return (
    <ConfigurationButton
      label={label}
      onClick={() => onClick(value)}
      icon={<span />}
      style={{
        padding: 'var(--space-s) var(--space-s)',
      }}
    />
  );
};
